import type { GroupMessage } from '../../api/types.js';
import { formatDateTime } from './labels.js';

export type EvidenceRole = 'merchant_service' | 'group_member';

/**
 * 统一证据浮层的数据：快照证据（判定句 = evidence.quote，无判定说明）与看板规则证据
 * （判定说明 = 后端现算的 verdict.text）共用同一个壳，消息节选都是锚点前后各 3 条。
 */
export interface EvidenceSheetData {
  title: string;
  anchorMessageId: number;
  /** 快照证据的原话；规则证据没有引文时为 null。 */
  quote: string | null;
  /** 判定说明（代码按规则生成，不让模型写）；快照证据为 null。 */
  verdict: string | null;
  messages: Array<GroupMessage & { role?: EvidenceRole }>;
  loading: boolean;
  error?: string | null;
}

/**
 * 证据浮层：标题 · 来源说明 · 消息节选（我方/群成员分列，我方标账号名）· 标红判定句 ·
 * 判定说明。role 由后端标注（出站或内部名单入站 = 我方）；快照证据接口不带 role 时按方向兜底。
 */
export function EvidenceSheet({ data, onClose }: { data: EvidenceSheetData; onClose(): void }) {
  return (
    <aside className="analysis-evidence-sheet" aria-label="证据原文">
      <header>
        <strong>{data.title}</strong>
        <button type="button" className="button ghost" onClick={onClose}>关闭</button>
      </header>
      <p className="evidence-source">微信聊天记录 · 逐字 · 标红 = 判定依据</p>
      {data.quote ? <q>{data.quote}</q> : null}
      {data.loading ? <p className="analysis-muted">加载上下文…</p> : null}
      {data.error ? <p className="analysis-note">{data.error}</p> : null}
      <ul>
        {data.messages.map((message) => {
          const role = message.role ?? (message.direction === 'out' ? 'merchant_service' : 'group_member');
          const anchor = message.id === data.anchorMessageId;
          return (
            <li key={message.id} className={`${role === 'merchant_service' ? 'service' : 'member'}${anchor ? ' anchor' : ''}`}>
              <div>
                <strong>
                  <span className={`evidence-role role-${role}`}>{role === 'merchant_service' ? '我方' : '群成员'}</span>
                  {message.direction === 'out' ? '工作号' : message.senderNickname || message.senderWechatId}
                </strong>
                <time>{formatDateTime(message.createdAt)}</time>
              </div>
              <p>{message.content || `[${message.mediaType ?? '非文本消息'}]`}</p>
              {anchor && data.verdict ? <em className="evidence-verdict">{data.verdict}</em> : null}
            </li>
          );
        })}
      </ul>
      {!data.loading && !data.error && data.messages.length === 0 ? <p className="analysis-muted">没有可展示的消息节选</p> : null}
    </aside>
  );
}
